import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BASE_URL } from '../constants';
import { cardPlace } from '../utils/hometown';
import { displayTeamName } from '../utils/teamNames';
import IndexShell from './ForBusiness/Dashboard/IndexShell';
import { PlayerIndexCard, cardMetaLine } from './ForBusiness/Dashboard/AllAthleteCard';
import { PageHeader, Card, Btn, Badge, SearchBox, Select, Input, Chip, Loading, EmptyState } from './ForBrands/ui/BrandUI';

const PAGE_SIZE = 24;
const ALL_SPORTS = 'All Players';
const SUBTITLE_NOTE = 'Short profiles only; log in for the full picture.';

// Same filter fields as the Player Index drawer (AllAthletes).
const FILTER_CONFIG = [
  { label: 'First Name', name: 'name', type: 'input' },
  { label: 'Last name', name: 'surname', type: 'input' },
  { label: 'Cachescore', name: 'cacheScore', type: 'input', inputType: 'number' },
  { label: 'Gender', name: 'gender', type: 'select', optionKey: 'gender' },
  { label: 'State', name: 'state', type: 'select', optionKey: 'state' },
  { label: 'City', name: 'city', type: 'input' },
  { label: 'School name', name: 'schoolName', type: 'input' },
];

// A row's team (its club) by the one display rule (utils/teamNames, D15):
// the row's teamDisplayName when the index sends one, else the name with
// no season CACHE added. "" without a team.
export const indexTeamName = (athlete) => {
  const name = String(athlete?.team || '').trim();
  if (!name && !athlete?.teamDisplayName) return '';
  return String(displayTeamName({ teamName: name, name, displayName: athlete.teamDisplayName, ageGroup: athlete.teamAgeGroup }) || name).trim();
};

// Public, no-login athlete index (/p/business/dashboard). Mirrors the
// business Player Index: same population (photo + not opted out of the
// index), same search + filter drawer, same V2 card. Public rows already
// carry the sport, school and state names; cards open the public share
// profile (/p/:athleteId).
const PublicAthleteCard = ({ athlete, onClick }) => {
  const fullName = [athlete.name, athlete.surname].filter(Boolean).join(' ');
  return (
    <PlayerIndexCard
      name={fullName}
      avatar={athlete.avatar}
      alt={fullName}
      sport={athlete.sport}
      jersey={athlete.jerseyNumber}
      // "Basha High School · Chandler, AZ" when the row carries the
      // formatted hometown, else "School · <state name>".
      meta={cardMetaLine(athlete.school || indexTeamName(athlete), cardPlace(athlete, athlete.state))}
      score={athlete.cacheScore ?? null}
      confidence={athlete.cacheScoreConfidence || ''}
      noScoreLabel="Not public"
      hoverScore
      onClick={onClick}
    />
  );
};

// "1,234 athletes on CACHE." once the index has answered; no number before
// that (or after a failed first page), so the page never shows a made-up count.
export const indexSubtitle = (total) => {
  if (total == null) return `Athletes on CACHE. ${SUBTITLE_NOTE}`;
  const noun = total === 1 ? 'athlete' : 'athletes';
  return `${total.toLocaleString('en-US')} ${noun} on CACHE. ${SUBTITLE_NOTE}`;
};

// Topbar right side: the V2 header's Player Index label, then the two ways
// in for a visitor.
const PublicTopbarActions = () => (
  <>
    <span className="hidden bm-overline sm:inline">Player Index</span>
    <span className="hidden h-5 w-px bg-bm-border sm:inline" />
    <Btn variant="secondary" to="/login">Log in</Btn>
    <Btn to="/register">Join CACHE</Btn>
  </>
);

// One field of the filter row: a text input (Enter applies) or a code select.
const FilterField = ({ filter, value, onChange, onEnter }) => (
  filter.type === 'input' ? (
    <Input
      type={filter.inputType || 'text'}
      value={value || ''}
      onChange={(e) => onChange(e.target.value || undefined)}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onEnter(); } }}
      placeholder={filter.label}
      aria-label={filter.label}
    />
  ) : (
    <Select value={value || ''} onChange={(e) => onChange(e.target.value || undefined)} aria-label={filter.label}>
      <option value="">{filter.label}</option>
      {filter.options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </Select>
  )
);

const PublicAthletesDashboard = () => {
  const navigate = useNavigate();
  const [athletes, setAthletes] = useState([]);
  const [sports, setSports] = useState([ALL_SPORTS]);
  const [filterOptions, setFilterOptions] = useState({ gender: [], state: [] });
  // null until the index answers, so the header never shows a made-up count.
  const [total, setTotal] = useState(null);
  // The page to fetch, a fresh object per request, so asking for the same
  // page again (Load more after a failed one) runs the fetch again.
  const [request, setRequest] = useState({ page: 1 });
  // The last page that answered: Load more asks for the one after it, so a
  // page that failed is asked for again rather than skipped.
  const [loadedPage, setLoadedPage] = useState(0);
  const [selectedSport, setSelectedSport] = useState(ALL_SPORTS);
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Applied criteria: the fetch runs from these, not the live inputs, so
  // search/filters apply on button press or Enter (same as the Player Index).
  const [applied, setApplied] = useState({ search: '', filters: {}, sport: ALL_SPORTS });
  const [loading, setLoading] = useState(true);

  const applyNow = (overrides = {}) => {
    setRequest({ page: 1 });
    setApplied({
      search: overrides.search !== undefined ? overrides.search : searchText,
      filters: overrides.filters !== undefined ? overrides.filters : filters,
      sport: overrides.sport !== undefined ? overrides.sport : selectedSport,
    });
  };

  useEffect(() => {
    let cancelled = false;
    const { page } = request;
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
    if (applied.search) params.set('search', applied.search);
    if (applied.sport && applied.sport !== ALL_SPORTS) params.set('sport', applied.sport);
    const activeFilters = Object.fromEntries(
      Object.entries(applied.filters || {}).filter(([, v]) => v),
    );
    if (Object.keys(activeFilters).length) params.set('searchFields', JSON.stringify(activeFilters));
    // An error answer and a network error are the same failure.
    const failed = {};
    fetch(`${BASE_URL}/api/users/public/athletes/?${params}`)
      .then((r) => (r.ok ? r.json() : failed))
      .catch(() => failed)
      .then((d) => {
        if (cancelled) return;
        if (d === failed) {
          // A failed first page (a new query) empties the index and its
          // count; a failed Load more keeps the cards and count on screen.
          if (page === 1) { setAthletes([]); setTotal(null); }
          return;
        }
        setLoadedPage(page);
        setAthletes((prev) => (page === 1 ? d.athletes || [] : [...prev, ...(d.athletes || [])]));
        setTotal(d.total ?? null);
        if (Array.isArray(d.sports) && d.sports.length) setSports(d.sports);
        if (d.filterOptions) setFilterOptions(d.filterOptions);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [request, applied]);

  const filterGroups = useMemo(
    () => FILTER_CONFIG.map((f) =>
      f.type === 'select' ? { ...f, options: filterOptions[f.optionKey] || [] } : f,
    ),
    [filterOptions],
  );
  const activeFilters = Object.entries(applied.filters || {}).filter(([, v]) => v);
  const activeCount = activeFilters.length;
  const hasMore = total !== null && athletes.length < total;

  const clearSearch = () => { setSearchText(''); applyNow({ search: '' }); };
  const clearAll = () => { setSearchText(''); setFilters({}); applyNow({ search: '', filters: {} }); };
  // The empty state's way out: drop the search, the filters and the sport.
  const hasCriteria = activeCount > 0 || !!applied.search || applied.sport !== ALL_SPORTS;
  const clearEverything = () => {
    setSelectedSport(ALL_SPORTS);
    setSearchText('');
    setFilters({});
    applyNow({ search: '', filters: {}, sport: ALL_SPORTS });
  };
  const removeFilter = (key) => {
    const next = { ...filters };
    delete next[key];
    setFilters(next);
    applyNow({ filters: next });
  };
  const filterChipLabel = (key, value) => {
    const config = filterGroups.find((f) => f.name === key);
    const option = config?.options?.find((o) => String(o.value) === String(value));
    return `${config?.label || key}: ${option ? option.label : value}`;
  };

  return (
    <IndexShell homeTo="/p/business/dashboard" right={<PublicTopbarActions />}>
      <PageHeader eyebrow="Athletes" title="Player index" subtitle={indexSubtitle(total)} />

      {/* Sport, search and filters: the signed-in Player Index card */}
      <Card padding="p-5" className="mb-4 w-full min-w-0 max-w-full">
        <form className="flex flex-wrap items-center gap-3" onSubmit={(e) => { e.preventDefault(); applyNow(); }}>
          <Select
            value={selectedSport}
            onChange={(e) => { setSelectedSport(e.target.value); applyNow({ sport: e.target.value }); }}
            aria-label="Sport"
            className="w-full sm:w-[200px]"
          >
            {sports.map((sport) => (
              <option key={sport} value={sport}>
                {sport === ALL_SPORTS ? 'All sports' : sport}
              </option>
            ))}
          </Select>
          <SearchBox
            aria-label="Search athletes"
            value={searchText}
            onChange={setSearchText}
            placeholder="Search by name, school, team or city, then press Enter"
            className="min-w-[240px] flex-1"
          />
          <Btn variant="secondary" type="submit">Search</Btn>
          <Btn variant={drawerOpen ? 'primary' : 'secondary'} onClick={() => setDrawerOpen(!drawerOpen)} aria-expanded={drawerOpen}>
            Filters
            {activeCount > 0 && (
              <Badge tone={drawerOpen ? 'onDark' : 'brand'} className="ml-2">{activeCount}</Badge>
            )}
          </Btn>
        </form>

        {drawerOpen && (
          <div className="mt-4 border-t border-bm-border pt-4">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {filterGroups.map((filter) => (
                <FilterField
                  key={filter.name}
                  filter={filter}
                  value={filters[filter.name]}
                  onChange={(value) => setFilters({ ...filters, [filter.name]: value })}
                  onEnter={() => applyNow()}
                />
              ))}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Btn onClick={() => applyNow()}>Apply filters</Btn>
              {activeCount > 0 && <Btn variant="link" onClick={clearAll}>Clear all</Btn>}
            </div>
          </div>
        )}

        {(activeCount > 0 || applied.search) && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="bm-label-xs text-bm-tertiary">Active</span>
            {applied.search && (
              <Chip active onClick={clearSearch} title="Remove this search">
                Search: "{applied.search}" &times;
              </Chip>
            )}
            {activeFilters.map(([key, value]) => (
              <Chip key={key} active onClick={() => removeFilter(key)} title="Remove this filter">
                {filterChipLabel(key, value)} &times;
              </Chip>
            ))}
            <Btn variant="link" size="sm" onClick={clearAll}>Clear all</Btn>
          </div>
        )}
      </Card>

      {/* Grid */}
      <div className="mt-6 lg:mt-12">
        {loading && athletes.length === 0 ? (
          <Loading label="Loading athletes..." />
        ) : athletes.length === 0 ? (
          <EmptyState
            action={hasCriteria && (
              <Btn variant="secondary" onClick={clearEverything}>Clear filters</Btn>
            )}
          >
            <span className="block bm-label-l text-bm-ink">No athletes found</span>
            Try a different search or filter.
          </EmptyState>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
              {athletes.map((a) => (
                <PublicAthleteCard key={a.athleteId} athlete={a} onClick={() => navigate(`/p/${a.athleteId}`)} />
              ))}
            </div>
            {hasMore && (
              <div className="mt-10 flex justify-center">
                <Btn
                  variant="secondary"
                  disabled={loading}
                  onClick={() => setRequest({ page: loadedPage + 1 })}
                >
                  {loading ? 'Loading...' : 'Load more athletes'}
                </Btn>
              </div>
            )}
          </>
        )}

        <p className="pt-10 text-center bm-label-xs text-bm-tertiary">Powered by CACHE</p>
      </div>
    </IndexShell>
  );
};

export default PublicAthletesDashboard;
